import { useState, useEffect } from "react";
import { useTheme, Box, Typography, CircularProgress, Alert, Paper, ButtonGroup, Button, useMediaQuery } from "@mui/material";
import { tokens } from "../../theme";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { API_BASE_URL } from '../../config/api';

const SpsBurnBarChart = ({ days = 14 }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalBurned, setTotalBurned] = useState(0);
  const [selectedTimeframe, setSelectedTimeframe] = useState(days);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Verfügbare Zeiträume
  const timeframes = [
    { label: '14D', value: 14 },
    { label: '30D', value: 30 },
    { label: '90D', value: 90 },
    { label: '1Y', value: 365 }
  ];

  // Jahresauswahl
  const years = [2022, 2023, 2024, 2025, 2026];
  const [selectedYear, setSelectedYear] = useState(null);

  // Zeitraum ändern (Tage oder Jahr)
  const handleTimeframeChange = (value) => {
    setSelectedTimeframe(value);
    setSelectedYear(null); // Wenn manuell Tage gewählt werden, Jahr zurücksetzen
  };
  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSelectedTimeframe(null); // Wenn Jahr gewählt, Tage zurücksetzen
  };

  // Format numbers in millions/billions
  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(Math.round(value * 100) / 100);
  };

  // Daten für das Diagramm aus der zentralen JSON-Datei laden
  useEffect(() => {
    setLoading(true);
    let apiUrl;
    if (selectedYear) {
      // Hole das komplette Jahr
      apiUrl = `${API_BASE_URL}/v1/sps-burns/burn-data/?from=${selectedYear}-01-01&to=${selectedYear}-12-31&nocache=${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    } else {
      // Hole die letzten N Tage
      apiUrl = `${API_BASE_URL}/v1/sps-burns/burn-data/?days=${selectedTimeframe}&nocache=${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    }
    fetch(apiUrl)
      .then(res => {
        if (!res.ok) throw new Error('Error loading data');
        return res.json();
      })
      .then(json => {

        // Nutze results (DRF-Standard), data (Fallback), oder json direkt wenn es ein Array ist
        const arr = Array.isArray(json) ? json : (Array.isArray(json.results) ? json.results : (Array.isArray(json.data) ? json.data : []));
        if (arr.length > 0) {
          // Stelle sicher, dass wir nur Einträge für das ausgewählte Jahr anzeigen, wenn ein Jahr ausgewählt wurde
          const filteredArr = selectedYear ? 
            arr.filter(entry => new Date(entry.date).getFullYear() === selectedYear) : 
            arr;
            
          if (filteredArr.length === 0) {
            setChartData([]);
            setError(`No SPS burn data available for year ${selectedYear}.`);
            setTotalBurned(0);
            return;
          }
          
          // Die API liefert die Daten im chronologisch aufsteigenden Format (älteste zuerst)
          // Sortiere sie, um sie chronologisch korrekt darzustellen (älteste links, neueste rechts)
          const sortedData = filteredArr.slice().sort((a, b) => new Date(a.date) - new Date(b.date));
          setChartData(sortedData);
          // Berechne die Summe der täglichen Burns für den angezeigten Zeitraum
          const total = sortedData.reduce((sum, entry) => sum + (entry.dailyBurns || 0), 0);
          // Verwende den aktuellen kumulierten Wert, falls verfügbar
          const latestEntry = sortedData[sortedData.length - 1];
          setTotalBurned(total);
          setError(null);
        } else {
          setChartData([]);
          setError('No SPS burn data available for the selected period.');
        }
      })
      .catch(err => {
        setError('Error loading data: ' + err.message);
        setChartData([]);
      })
      .finally(() => setLoading(false));
  }, [selectedTimeframe, selectedYear]);

  // Update CustomTooltip to display both cumulative and daily burned SPS
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { totalBurned, dailyBurns, date } = payload[0].payload;
      return (
        <Paper 
          sx={{ 
            backgroundColor: colors.primary[700],
            p: 1.5,
            border: `1px solid ${colors.primary[500]}`,
            boxShadow: theme.shadows[3]
          }}
        >
          <Typography variant="body2" sx={{ color: colors.grey[100], fontWeight: 'bold' }}>
            {date}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.redAccent[400] }}>
            Cumulative Burned: {formatNumber(totalBurned)} SPS
          </Typography>
          <Typography variant="body2" sx={{ color: colors.greenAccent[400], mt: 1 }}>
            Daily Burned: {formatNumber(dailyBurns)} SPS
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box height="100%">
      <Box 
        display="flex" 
        flexDirection={isMobile ? "column" : "row"} 
        alignItems={isMobile ? "flex-start" : "center"} 
        justifyContent="space-between" 
        mb={2}
      >
        <Box display="flex" alignItems="center" mb={isMobile ? 1 : 0}>
          <LocalFireDepartmentIcon sx={{ color: colors.redAccent[500], fontSize: '1.8rem', mr: 1 }} />
          <Typography variant="h5" fontWeight="bold">
            SPS BURNED
          </Typography>
        </Box>
        
        <Box 
          display="flex" 
          flexDirection={isMobile ? "column" : "row"} 
          alignItems={isMobile ? "flex-start" : "center"}
          gap={isMobile ? 1 : 2}
        >
          {/* Zeitraumauswahl */}
          <ButtonGroup 
            size="small" 
            variant="outlined" 
            aria-label="timeframe selection"
            sx={{ 
              mb: isMobile ? 1 : 0,
              '.MuiButtonGroup-grouped': {
                borderColor: colors.grey[700],
              }
            }}
          >
            {timeframes.map((timeframe) => (
              <Button
                key={timeframe.value}
                onClick={() => handleTimeframeChange(timeframe.value)}
                sx={{
                  color: selectedTimeframe === timeframe.value ? colors.greenAccent[400] : colors.grey[300],
                  borderColor: colors.grey[700],
                  backgroundColor: selectedTimeframe === timeframe.value ? colors.primary[600] : 'transparent',
                  '&:hover': {
                    backgroundColor: colors.primary[600],
                    borderColor: colors.greenAccent[400],
                  },
                  px: isMobile ? 1 : 2,
                  minWidth: isMobile ? 40 : 50,
                }}
              >
                {timeframe.label}
              </Button>
            ))}
          </ButtonGroup>
          
          {/* Jahresauswahl */}
          <ButtonGroup size="small" variant="outlined" sx={{ ml: 2, mb: isMobile ? 1 : 0 }}>
            {years.map((year) => (
              <Button
                key={year}
                onClick={() => handleYearChange(year)}
                sx={{
                  color: selectedYear === year ? colors.greenAccent[400] : colors.grey[300],
                  borderColor: colors.grey[700],
                  backgroundColor: selectedYear === year ? colors.primary[600] : 'transparent',
                  '&:hover': {
                    backgroundColor: colors.primary[600],
                    borderColor: colors.greenAccent[400],
                  },
                  px: isMobile ? 1 : 2,
                  minWidth: isMobile ? 40 : 50,
                }}
              >
                {year}
              </Button>
            ))}
          </ButtonGroup>

          <Box display="flex" alignItems="center">
            <Typography variant="body2" color={colors.grey[300]} fontSize={isMobile ? "0.75rem" : "0.875rem"}>
              {isMobile ? "BURNED:" : selectedYear ? `TOTAL BURNED IN ${selectedYear}:` : `BURNED IN ${selectedTimeframe} DAYS:`}
            </Typography>
            <Typography variant="h6" color={colors.redAccent[500]} fontWeight="bold" ml={1} fontSize={isMobile ? "1rem" : "1.25rem"}>
              +{formatNumber(totalBurned)} SPS
            </Typography>
          </Box>
        </Box>
      </Box>

      {error ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={350}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[800]} vertical={false} />
            <XAxis 
              dataKey="date" 
              stroke={colors.grey[300]}
              tickSize={5}
              tickMargin={5}
              tick={{ fontSize: 11 }}
              tickFormatter={(date) => {
                try {
                  const dateObj = new Date(date);
                  return dateObj.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: selectedTimeframe <= 90 ? 'numeric' : undefined,
                    year: selectedYear ? undefined : (selectedTimeframe > 90 ? '2-digit' : undefined)
                  });
                } catch (e) {
                  return date;
                }
              }}
              interval={selectedYear ? 
                Math.floor(chartData.length / 12) : // Für Jahresansicht etwa einen Monat pro Tick
                (selectedTimeframe > 90 ? Math.floor(selectedTimeframe / 30) : 
                 selectedTimeframe > 30 ? Math.floor(selectedTimeframe / 15) : 'preserveStartEnd')}
            />
            <YAxis
              stroke={colors.grey[300]}
              tickFormatter={(value) => formatNumber(value)}
              tick={{ fontSize: 11 }}
              domain={['auto', 'auto']}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ bottom: 0 }} />
            <Bar 
              dataKey="dailyBurns" 
              name="Daily Burned SPS" 
              fill={colors.redAccent[500]} 
              radius={[4, 4, 0, 0]}
              barSize={selectedTimeframe > 90 ? 2 : selectedTimeframe > 30 ? 5 : 30}
              animationDuration={1000}
              strokeWidth={0}
              fillOpacity={0.9}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Box 
          display="flex" 
          flexDirection="column" 
          justifyContent="center" 
          alignItems="center" 
          height="300px"
        >
         <HelpOutlineIcon sx={{ fontSize: '3rem', color: colors.grey[500] }} />
          <Typography variant="body1" color={colors.grey[300]}>
            No data available for the selected period
          </Typography>
          <Typography variant="body2" color={colors.grey[500]} sx={{ mt: 1 }}>
            Please check the data source or try a different time frame.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SpsBurnBarChart;